import { BaseType, drag, select, Selection } from "d3";

import { roundToGrid } from "@examspark/common/dist/utils/number-line";
import { Cartesian } from "@examspark/types";

import { WithIndex } from "../../cartesian/CartesianGraph";
import { D3NumberLine, NumberLineCoordinate } from "../d3Utils";

const coordinatesForShape = (shape: Cartesian.Shape) =>
  "coords" in shape ? shape.coords : [shape.coord];

export const colorDecorator = <GType extends BaseType>(
  d3SvgElement: Selection<GType, any, any, any>,
) => {
  d3SvgElement
    .attr("fill", "#1fa4e1")
    .attr("stroke", "#1fa4e1")
    .attr("stroke-width", 2);
};

export const createCoordinate = <GType extends BaseType>(
  axis: D3NumberLine,
  onMove?: (
    shapeIndex: number,
    coordIndex: number,
    coordinate: Cartesian.Coordinate,
  ) => void,
) => {
  return (selection: Selection<GType, NumberLineCoordinate, any, any>) => {
    const dragHandleDecorator = drag<any, NumberLineCoordinate>()
      // subject is where we start, and needs to be in pixel space
      .subject((_, d) => ({
        x: axis.scale(d.x),
        y: 0,
      }))
      .on("drag", (e, data) => {
        if (data.shapeIndex == null || data.index == null) {
          console.log("missing data props on drag");
          return;
        }
        const coordToSave = {
          x: roundToGrid(axis.scale.invert(e.x), axis),
          y: 0,
        };

        onMove?.(data.shapeIndex, data.index, coordToSave);
      });

    selection
      .call(colorDecorator)
      .call(dragHandleDecorator)
      .attr("cx", (d) => axis.scale(d.x))
      .attr("cy", 0)
      .on("mouseover", function (d, i) {
        select(this)
          .transition()
          .duration(100)
          .attr("fill-opacity", 0.1)
          .attr("stroke-opacity", 1);
      })
      .on("mouseout", function (d, i) {
        select(this)
          .transition()
          .duration(300)
          .attr("fill-opacity", 0)
          .attr("stroke-opacity", 0);
      })
      .attr("fill-opacity", 0)
      .attr("stroke-opacity", 0)
      .attr("stroke-dasharray", "5,5")
      .attr("r", 23);
  };
};

// createCoordine, without the mouseover or onMove (just a red dot)
export const createListenPointCoordinate = (axis: D3NumberLine) => {
  return (selection: Selection<any, NumberLineCoordinate, any, any>) => {
    selection
      .call(colorDecorator)
      .attr("cx", (d) => axis.scale(d.x))
      .attr("cy", 0)
      .attr("fill-opacity", 0.1)
      .attr("stroke-opacity", 1)
      .attr("r", 23)
      .attr("stroke-dasharray", "5,5")
      .attr("color", "red");
  };
};

export const useShapesDecorator =
  ({
    shapes,
    axis,
    onMove,
    onErase,
  }: {
    shapes: Cartesian.Shape[];
    axis: D3NumberLine;
    onMove: (
      shapeIndex: number,
      coordIndex: number,
      coordinate: Cartesian.Coordinate,
    ) => void;
    onErase: (shapeIndex: number) => void;
  }) =>
  <GType extends BaseType>(
    svg: Selection<GType, WithIndex<Cartesian.Shape>, any, any>,
  ) => {
    const shapeToCoords = (shape: Cartesian.Shape, shapeIndex: number) =>
      coordinatesForShape(shape).map((coord, index) => ({
        ...coord,
        shapeIndex,
        index,
        is_open:
          shape.type === "ray-left-open" ||
          shape.type === "ray-right-open" ||
          (shape.type === "line-segment-left-open" && index === 0) ||
          (shape.type === "line-segment-right-open" && index === 1) ||
          shape.type === "line-segment-both-open",
      }));

    svg
      .selectAll("circle.shape-coord-center")
      .data(shapeToCoords)
      .join("circle")
      .raise()
      .attr("class", "shape-coord-center")
      .attr("r", 4)
      .attr("cx", (coord) => axis.scale(coord.x))
      // .attr("cy", (coord) => y.scale(coord.y))
      .call(colorDecorator)
      .attr("fill", (d) => (d.is_open ? "#fff" : "#1fa4e1"));

    svg
      .selectAll("circle.shape-coords")
      .data<NumberLineCoordinate>(shapeToCoords)
      .join("circle")
      .raise()
      .attr("class", "shape-coords")
      .call(createCoordinate(axis, onMove))
      .on("click", (e, d) => {
        if (onErase && d.shapeIndex != null) {
          onErase(d.shapeIndex);
        }
      });

    const lineStrokeWidth = 4;
    const lineStroke = "#1fa4e1";

    svg.each(function shape(shape: Cartesian.Shape) {
      //
      const shapeSvg = select(this);

      shapeSvg.select(".outline").remove();
      const outlineSvg = shapeSvg.append("g").attr("class", "outline").lower();

      switch (shape.type) {
        case "point":
          break;

        case "ray-left":
        case "ray-left-open":
        case "ray-right":
        case "ray-right-open":
          {
            const is_left = shape.type.indexOf("left") !== -1;

            outlineSvg
              .append("line")
              .attr("marker-end", "url(#arrow)")
              .attr("x1", axis.scale(shape.coord.x))
              .attr("y1", 0)
              .attr(
                "x2",
                is_left
                  ? axis.scale(axis.scale.domain()[0])
                  : axis.scale(axis.scale.domain()[1]),
              )
              .attr("y2", 0)
              .attr("stroke", lineStroke)
              .attr("stroke-width", lineStrokeWidth);
          }
          break;

        case "line-segment":
        case "line-segment-left-open":
        case "line-segment-right-open":
        case "line-segment-both-open":
          outlineSvg
            .append("line")
            .attr("x1", axis.scale(shape.coords[0].x))
            .attr("y1", 0)
            .attr("x2", axis.scale(shape.coords[1].x))
            .attr("y2", 0)
            .attr("stroke", lineStroke)
            .attr("stroke-width", lineStrokeWidth);
          break;
      }
    });
  };
